import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useLang } from "@/lib/i18n";

export const HeroDevice = () => {
  const { t } = useLang();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateY = useTransform(sx, [-1, 1], [-14, 14]);
  const rotateX = useTransform(sy, [-1, 1], [10, -10]);
  const shiftX = useTransform(sx, [-1, 1], [-18, 18]);
  const shiftY = useTransform(sy, [-1, 1], [-12, 12]);

  useEffect(() => {
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth) * 2 - 1);
      my.set((e.clientY / window.innerHeight) * 2 - 1);
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [mx, my]);

  const Tag = ({ label, value, className, float, z }) => (
    <motion.div
      style={{ translateZ: z }}
      className={`absolute rounded-2xl border border-line bg-white/95 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(20,33,61,0.4)] backdrop-blur ${className}`}
      animate={{ y: float }}
      transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-coral">{label}</p>
      <p className="mt-1 text-sm font-semibold text-navy">{value}</p>
    </motion.div>
  );

  return (
    <motion.div
      className="relative mx-auto h-[420px] w-[300px] sm:h-[520px] sm:w-[360px]"
      style={{ perspective: 1400 }}
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
      data-testid="hero-device"
    >
      <motion.div style={{ x: shiftX, y: shiftY }} className="absolute -left-6 top-10 h-40 w-40 rounded-full bg-brand/10 blur-3xl" />
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <div className="absolute inset-0 rounded-[2.6rem] border border-navy/10 bg-white shadow-[0_40px_90px_-30px_rgba(20,33,61,0.45)]">
          <div className="absolute inset-[10px] overflow-hidden rounded-[2rem] bg-navy">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
            <div className="absolute left-1/2 top-3 h-6 w-24 -translate-x-1/2 rounded-full bg-black/50" />
            <div className="absolute inset-x-6 top-16 space-y-3">
              <div className="h-2 w-16 rounded-full bg-coral" />
              <div className="h-7 w-40 rounded-md bg-white/85" />
              <div className="h-2 w-52 rounded-full bg-white/25" />
            </div>
            <div className="absolute inset-x-6 top-40 grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="h-24 rounded-xl border border-white/15 bg-white/10"
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 3.2, delay: i * 0.4, repeat: Infinity, ease: "easeInOut" }}
                />
              ))}
            </div>
            <div className="absolute inset-x-6 bottom-8 h-12 rounded-full bg-brand" />
          </div>
        </div>
        <Tag label={t.device.tag1Label} value={t.device.tag1Value} className="-right-8 top-24" float={[0, -10, 0]} z={80} />
        <Tag label={t.device.tag2Label} value={t.device.tag2Value} className="-left-10 bottom-24" float={[0, 10, 0]} z={60} />
      </motion.div>
    </motion.div>
  );
};
